import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

/**
 * Guards customer-only pages (e.g. My Bookings) that assume a linked customer
 * profile. Non-customer accounts (admin/manifest/etc.) hitting these routes via
 * a direct URL would otherwise fire customer-scoped API calls that 4xx and blank
 * the page (Bug 035).
 *
 * Three states — order matters:
 *   1. auth still loading   → spinner. NEVER redirect mid-load: on a hard
 *      refresh a legitimate customer's `user` is momentarily null while the
 *      session restores, and redirecting here would bounce them to the
 *      dashboard incorrectly.
 *   2. authenticated, no customer profile → redirect to the staff dashboard.
 *   3. authenticated customer → render.
 *
 * Gate on customerProfile PRESENCE (`hasCustomerProfile`), NOT a role string —
 * custom roles are planned, so `role === 'customer'` checks will rot.
 */
const CustomerRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.hasCustomerProfile) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return children;
};

export default CustomerRoute;
